/**
 * One vendor asset, rendered according to what KIND of thing it is.
 *
 * The frame takes no `height`, `width`, `objectFit`, `aspect` or `radius`.
 * Every one of those follows from `kind` and `scale` through the media policy
 * table, which is the whole point: eleven hard-coded hero heights and nine
 * hard-coded container widths in two pillar files were each a prediction about
 * a width the layout owns, and predicting is what produced the defect. This
 * frame measures its own box with one `ResizeObserver` instead.
 *
 * Three rules it enforces that nothing above it can opt out of:
 *   · the ratio is reserved BEFORE the asset loads, so nothing reflows;
 *   · a raster is never scaled above 1:1 of its natural size — the caller's
 *     declared intrinsics where it has them, and otherwise the decoded
 *     bitmap's own, which the frame reads on load rather than assuming a
 *     300px proxy thumbnail is fit to be a 528px hero;
 *   · a failure is a designed, semantic state in the same frame box, so a
 *     grid of covers never re-lays itself out when one host declines.
 *
 * `overlay` is a SIBLING of the activation trigger, never a child of it. An
 * identity placed over a photograph is part of the item, not part of the
 * picture, and pressing it has to open the item — which it cannot do from
 * inside the button that opens the lightbox. It also sits outside the clipped
 * frame, so a badge can straddle the frame's own edge instead of a pillar
 * hand-lifting it with a negative margin against a padding it does not own.
 */
"use client";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import { Cimage, CIMAGE_ANIMATION } from "@/components/ui/Cimage";
import { ButtonPreset } from "@/constants/componentPresets";
import {
  CRC_MEDIA_LOAD_TIMEOUT_MS,
  CRC_MEDIA_POLICY,
  CRC_MEDIA_REFERRER_POLICY,
  CresultcardMediaKind,
  resolveCresultcardAspect,
  resolveCresultcardNativeHeight,
} from "./cresultcard.media";
import {
  useCresultcardNestedRadius,
  useCresultcardRadius,
} from "./cresultcard.radius";
import {
  CRC_MEDIA_PLATE_BACKGROUND_IMAGE,
  CRC_MEDIA_PLATE_SHADOW,
  CRC_TEXT_FAINT,
} from "./cresultcard.tokens";
import {
  CresultcardMarkAssetState,
  CresultcardMediaScale,
} from "./cresultcard.types";
import { CresultcardMediaFallback } from "./CresultcardMediaFallback";

/** A decoded 1×1 can only be a tracking-pixel response, never a real asset. */
const DEGENERATE_PIXEL_WIDTH = 1;
const CONTAINED_MARKER_LABEL = "contained";
const ARM_LABEL_PREFIX = "Play ";

/** The asset's own pixels, once the browser has decoded them. */
interface CresultcardNaturalSize {
  readonly width: number;
  readonly height: number;
}

/**
 * What one decode attempt concluded, and WHICH URL it concluded it about.
 *
 * The `src` travels with the verdict because a set swaps assets on a frame that
 * stays mounted: without it, one asset's failure is inherited by every asset the
 * reader steps to afterwards, and the fallback for a host that declined is
 * painted over a picture that would have loaded. It also makes the state
 * self-resetting, so there is no reset effect racing a cached image's `load`.
 */
interface CresultcardMediaOutcome {
  readonly src: string;
  readonly state: CresultcardMarkAssetState;
  readonly natural: CresultcardNaturalSize | null;
}

/** The frame's own box, so an overlay can straddle the clipped edge. */
const HOLDER_STYLE: CSSProperties = {
  position: "relative",
  display: "block",
  width: "100%",
  minWidth: 0,
};

const TRIGGER_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/Media/Trigger",
  "data-crc-control": true,
};

/** The trigger fills the frame exactly, so the hit area IS the picture. */
const FULL_FRAME_STYLE: CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  overflow: "hidden",
};

/** How much of the frame a mark leaves as air, so logos are not edge to edge. */
function opticalInset(kind: CresultcardMediaKind, width: number): number {
  return Math.round(width * CRC_MEDIA_POLICY[kind].opticalPadding);
}

export interface CresultcardMediaArm {
  /** What the armed thing is, for the poster control's accessible name. */
  readonly title: string;
  /** The player, mounted only once a reader has asked for it. */
  readonly render: () => ReactNode;
}

interface CresultcardMediaProps {
  readonly kind: CresultcardMediaKind;
  /** The vendor's own URL, verbatim. Absent is a first-class state. */
  readonly src?: string;
  readonly alt: string;
  readonly intrinsicWidth?: number;
  readonly intrinsicHeight?: number;
  readonly scale: CresultcardMediaScale;
  /** Chrome placed on top of the frame — badges, scrims, durations. */
  readonly overlay?: ReactNode;
  /** Opens the lightbox. Media inside an openable item opens the ASSET. */
  readonly onOpen?: () => void;
  /** Poster to player, in the same frame box, so the ratio never changes. */
  readonly arm?: CresultcardMediaArm;
  /** The item's own name, which is what the designed absence is built from. */
  readonly fallbackTitle: string;
  readonly fallbackSecondary?: string;
  /**
   * A pillar whose designed absence IS content — a date plate where the poster
   * would have been — puts it here and owns the whole face of the fallback.
   */
  readonly fallbackContent?: ReactNode;
  /**
   * Lets a composition substitute its own designed absence when a host
   * declines, instead of every call site re-deriving the same boolean.
   */
  readonly onState?: (state: CresultcardMarkAssetState) => void;
}

export function CresultcardMedia({
  kind,
  src,
  alt,
  intrinsicWidth,
  intrinsicHeight,
  scale,
  overlay,
  onOpen,
  arm,
  fallbackTitle,
  fallbackSecondary,
  fallbackContent,
  onState,
}: CresultcardMediaProps): ReactElement {
  const policy = CRC_MEDIA_POLICY[kind];
  const frameRef = useRef<HTMLSpanElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [outcome, setOutcome] = useState<CresultcardMediaOutcome | null>(null);
  const [armed, setArmed] = useState(false);
  const [timeoutEligible, setTimeoutEligible] = useState(
    () => typeof IntersectionObserver === "undefined",
  );
  /** A verdict about some other URL is not a verdict about this one. */
  const settled = outcome != null && outcome.src === src ? outcome : null;
  const loadState = settled?.state ?? CresultcardMarkAssetState.Loading;
  const natural = settled?.natural ?? null;
  /** No `src` is absence, not a load that has not finished yet. */
  const assetState = src == null ? CresultcardMarkAssetState.Absent : loadState;

  useEffect(() => {
    const frame = frameRef.current;
    if (frame == null || typeof ResizeObserver === "undefined") {
      return;
    }
    // One observer per frame. The alternative — telling the frame how wide it
    // is from above — is the thing the media contract exists to remove.
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry != null) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  useEffect((): (() => void) | undefined => {
    const frame = frameRef.current;
    if (frame == null) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setTimeoutEligible(true);
        observer.disconnect();
      }
    });
    observer.observe(frame);
    return (): void => observer.disconnect();
  }, []);

  useEffect((): void => {
    onState?.(assetState);
  }, [assetState, onState]);

  /**
   * A host that answers nothing at all still has to reach the designed absence.
   *
   * Keyed on `src` and on the state derived for it, so the timer is armed once
   * per URL, disarmed the moment that URL resolves or declines, and disarmed on
   * unmount. It cannot fire against a frame that already has a verdict, because
   * a frame that has one is not in `Loading` and the effect never arms.
   */
  useEffect((): (() => void) | undefined => {
    if (
      !timeoutEligible ||
      src == null ||
      loadState !== CresultcardMarkAssetState.Loading
    ) {
      return undefined;
    }
    const timer = window.setTimeout((): void => {
      setOutcome({
        src,
        state: CresultcardMarkAssetState.Failed,
        natural: null,
      });
    }, CRC_MEDIA_LOAD_TIMEOUT_MS);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [loadState, src, timeoutEligible]);

  const handleLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>): void => {
      if (src == null) {
        return;
      }
      // No `fallbackSrc` anywhere in the kit, which is what makes this check
      // safe: with no injected pixel, a decoded 1×1 can only be a host that
      // answered with a tracker, and it would otherwise render as a blank.
      const width = event.currentTarget.naturalWidth;
      const height = event.currentTarget.naturalHeight;
      if (width <= DEGENERATE_PIXEL_WIDTH) {
        setOutcome({
          src,
          state: CresultcardMarkAssetState.Failed,
          natural: null,
        });
        return;
      }
      setOutcome({
        src,
        state: CresultcardMarkAssetState.Resolved,
        natural: { width, height },
      });
    },
    [src],
  );

  const handleError = useCallback((): void => {
    if (src == null) {
      return;
    }
    setOutcome({ src, state: CresultcardMarkAssetState.Failed, natural: null });
  }, [src]);

  const handleArm = useCallback((): void => {
    setArmed(true);
  }, []);

  // The pixels the frame is allowed to take its SHAPE from: the caller's
  // declared capture always, and the decoded bitmap as well for the kinds whose
  // declared aspect was only ever a stand-in. A cover is excluded on purpose —
  // its 2:3 is reserved before paint and re-laying it out on load is the reflow
  // the media contract exists to prevent.
  const decodedShape = policy.adoptsDecodedShape ? natural : null;
  const shapeWidth = intrinsicWidth ?? decodedShape?.width;
  const shapeHeight = intrinsicHeight ?? decodedShape?.height;
  const aspect = resolveCresultcardAspect({
    kind,
    scale,
    intrinsicWidth: shapeWidth,
    intrinsicHeight: shapeHeight,
  });
  // Until the box has been measured there is no derived height to have, and the
  // reserved ratio above is what holds the space. Deriving from a width of zero
  // is a zero-height frame on first paint for every asset in the kind.
  const nativeHeight =
    policy.needsIntrinsic &&
    containerWidth > 0 &&
    shapeWidth != null &&
    shapeHeight != null
      ? resolveCresultcardNativeHeight({
          containerWidth,
          intrinsicWidth: shapeWidth,
          intrinsicHeight: shapeHeight,
          scale,
        })
      : null;
  const holderRadius = useCresultcardRadius();
  const nestedRadius = useCresultcardNestedRadius();
  // A bled asset has no inner inset, so it takes the holder's own rung rather
  // than the one below it.
  const bleeds = policy.bleedsAtHero && scale === CresultcardMediaScale.Hero;
  const radius = policy.circular
    ? undefined
    : bleeds
      ? holderRadius
      : nestedRadius;
  const inset = opticalInset(kind, containerWidth);
  const failed = assetState === CresultcardMarkAssetState.Failed;
  const showFallback = src == null || failed;
  // The declared pixels where the caller has them, the decoded bitmap's own
  // where it does not. A proxy thumbnail that arrives 240px wide is 240px wide
  // whether or not anybody wrote that down.
  //
  // The ceiling applies only to kinds whose intrinsic size is part of the
  // meaning. On a `cover` kind it is not restraint, it is a frame that can
  // never be filled: `object-fit: cover` cannot crop an image it is forbidden
  // to grow to, so the asset sits at 1:1 with bare plate around it.
  const capsToIntrinsic = policy.capsToIntrinsic;
  const ceilingWidth = capsToIntrinsic
    ? (intrinsicWidth ?? natural?.width)
    : undefined;
  const ceilingHeight = capsToIntrinsic
    ? (intrinsicHeight ?? natural?.height)
    : undefined;

  const frameStyle: CSSProperties = {
    position: "relative",
    display: "block",
    width: "100%",
    minWidth: 0,
    height: nativeHeight == null ? undefined : nativeHeight.height,
    aspectRatio: nativeHeight == null && aspect != null ? aspect : undefined,
    overflow: "hidden",
    borderRadius: policy.circular ? "50%" : radius,
    backgroundImage: CRC_MEDIA_PLATE_BACKGROUND_IMAGE,
    boxShadow: CRC_MEDIA_PLATE_SHADOW,
  };

  const asset = armed ? (
    arm?.render()
  ) : showFallback ? (
    <CresultcardMediaFallback
      secondary={fallbackSecondary}
      title={fallbackTitle}
    >
      {fallbackContent}
    </CresultcardMediaFallback>
  ) : (
    <span
      className="cresultcard__media-asset"
      data-el="Cresultcard/Media/Asset"
      data-crc-resolved={assetState === CresultcardMarkAssetState.Resolved}
      style={{
        position: "absolute",
        inset,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Cimage
        alt={alt}
        // Plain arrival, never a blur: covers, marks, diagrams and map tiles
        // all carry fine detail, and the kit owns the fade on the frame above.
        animation={CIMAGE_ANIMATION.None}
        height="100%"
        objectFit={policy.fit}
        onError={handleError}
        onLoad={handleLoad}
        referrerPolicy={CRC_MEDIA_REFERRER_POLICY}
        src={src ?? ""}
        style={{
          // Never upscaled past its own pixels where those pixels carry
          // meaning: a 100×100 cover blown up to a 236px card is a claim the
          // capture does not support. Where the crop IS the presentation the
          // ceiling is absent and the asset fills its frame.
          maxWidth: ceilingWidth,
          maxHeight: ceilingHeight,
        }}
        width="100%"
      />
    </span>
  );

  const frameContent = (
    <>
      {asset}
      {nativeHeight?.contained === true && (
        <span
          style={{
            position: "absolute",
            insetBlockEnd: 6,
            insetInlineEnd: 8,
            fontSize: 8,
            fontWeight: 660,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: CRC_TEXT_FAINT,
          }}
        >
          {CONTAINED_MARKER_LABEL}
        </span>
      )}
    </>
  );

  const activate = arm == null ? onOpen : handleArm;
  const activateLabel = arm == null ? alt : `${ARM_LABEL_PREFIX}${arm.title}`;
  const inert = armed || (onOpen == null && arm == null);

  return (
    <span
      data-el="Cresultcard/Media/Root"
      style={HOLDER_STYLE}
    >
      <span
        data-el="Cresultcard/Media/Viewport"
        ref={frameRef}
        style={frameStyle}
      >
        {inert ? (
          frameContent
        ) : (
          <Cbutton
            ariaLabel={activateLabel}
            containerStyle={FULL_FRAME_STYLE}
            contentStyle={FULL_FRAME_STYLE}
            disableClickMotion
            disableHoverEffects
            onClick={activate}
            padding={0}
            preset={ButtonPreset.Ghost}
            radius={radius ?? 0}
            rootAttributes={{
              ...TRIGGER_ROOT_ATTRIBUTES,
            }}
            style={FULL_FRAME_STYLE}
          >
            {frameContent}
          </Cbutton>
        )}
      </span>
      {armed ? null : overlay}
    </span>
  );
}
